import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Layers3, Loader2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { FACTORY_STATUSES, FACTORY_STATUS_COLORS } from "@/constants/workflow";

/**
 * Left side panel (mirrors the main model's bulk bar): factory stages as
 * ordered checkboxes. Checking a stage auto-checks every preceding stage —
 * a stage can never be checked without its predecessors. Unchecking a stage
 * unchecks every later stage. "Awaiting Cutting" is always checked.
 */
export function FactoryStatusPanel({ projectId, selection, panels, onDone, onClose }) {
  const [busy, setBusy] = useState(false);

  const panelsByUid = useMemo(() => {
    const map = new Map();
    (panels || []).forEach((panel) => map.set(panel.panel_uid, panel));
    return map;
  }, [panels]);

  // Shared starting point: the earliest stage every selected panel has reached.
  const minIndex = useMemo(() => {
    const indices = selection
      .map((uid) =>
        FACTORY_STATUSES.indexOf(
          panelsByUid.get(uid)?.factory_status || "Awaiting Cutting"
        )
      )
      .filter((i) => i >= 0);
    return indices.length ? Math.min(...indices) : 0;
  }, [selection, panelsByUid]);

  const [checked, setChecked] = useState(() => new Set(FACTORY_STATUSES.slice(0, 1)));
  const selectionKey = selection.join(",");
  useEffect(() => {
    setChecked(new Set(FACTORY_STATUSES.slice(0, minIndex + 1)));
  }, [minIndex, selectionKey]);

  const toggle = (stage) => {
    const idx = FACTORY_STATUSES.indexOf(stage);
    if (idx <= 0) return; // Awaiting Cutting is the mandatory first stage.
    if (checked.has(stage)) {
      // Later stages cannot stay checked without this one.
      setChecked(new Set(FACTORY_STATUSES.slice(0, idx)));
    } else {
      // Checking a stage checks the whole chain up to it.
      setChecked(new Set(FACTORY_STATUSES.slice(0, idx + 1)));
    }
  };

  const target = FACTORY_STATUSES[checked.size - 1] || FACTORY_STATUSES[0];
  const unchanged = checked.size - 1 === minIndex;

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/panels/factory-status/bulk`, {
        panel_uids: selection,
        status: target,
      });
      const failed = data.failed || [];
      if (failed.length) {
        const message = `${data.updated.length} updated · ${failed.length} blocked`;
        const options = { description: String(failed[0]?.detail || "") };
        if (data.updated.length) toast.warning(message, options);
        else toast.error(message, options);
      } else {
        toast.success(`${data.updated.length} panels → ${target}`);
      }
      onDone?.();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === "object" && detail?.msg ? detail.msg : apiError(error)
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside
      data-testid="factory-status-panel"
      className="okn-scroll absolute left-0 top-0 z-30 flex h-full w-[280px] max-w-full flex-col overflow-y-auto border-r border-white/10 bg-[#0D1526]/97 backdrop-blur-md"
    >
      <div className="sticky top-0 flex items-center justify-between gap-2 border-b border-white/10 bg-[#0D1526] px-4 py-4">
        <span
          data-testid="factory-selection-count"
          className="mono flex min-w-0 flex-col gap-0.5 text-cyan-300"
        >
          <span className="flex items-center gap-1.5 text-[12.5px] font-medium uppercase tracking-wider">
            <Layers3 size={14} /> Factory Status
          </span>
          <span className="text-[10.5px] uppercase tracking-wider text-slate-500">
            {selection.length} selected
          </span>
        </span>
        <button
          data-testid="factory-panel-close"
          onClick={onClose}
          className="grid h-7 w-7 place-items-center rounded-md text-slate-400 transition-colors hover:bg-white/10 hover:text-slate-200"
        >
          <X size={15} />
        </button>
      </div>

      <div className="flex-1 space-y-1 px-3 py-3">
        {FACTORY_STATUSES.map((stage, idx) => {
          const isChecked = checked.has(stage);
          const isFirst = idx === 0;
          return (
            <button
              key={stage}
              type="button"
              data-testid={`factory-stage-${stage.replace(/\s/g, "-")}`}
              data-checked={isChecked}
              disabled={busy || isFirst}
              onClick={() => toggle(stage)}
              className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                isChecked
                  ? "border-cyan-500/30 bg-cyan-500/10"
                  : "border-white/5 bg-white/[0.02] hover:bg-white/5"
              } ${isFirst ? "cursor-default" : ""}`}
            >
              <span
                className={`grid h-4.5 w-4.5 shrink-0 place-items-center rounded border transition-colors ${
                  isChecked
                    ? "border-cyan-400 bg-cyan-500 text-[#04121a]"
                    : "border-white/20 bg-transparent"
                }`}
                style={{ height: 18, width: 18 }}
              >
                {isChecked ? <Check size={12} strokeWidth={3} /> : null}
              </span>
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${FACTORY_STATUS_COLORS[stage] ? "" : "border border-white/30"}`}
                style={{ backgroundColor: FACTORY_STATUS_COLORS[stage] || "transparent" }}
              />
              <span
                className={`text-[12px] ${isChecked ? "text-slate-100" : "text-slate-400"}`}
              >
                {stage}
              </span>
            </button>
          );
        })}
        <p className="mono px-1 pt-2 text-[9.5px] uppercase tracking-[0.18em] text-slate-500">
          Stages unlock in order — checking one checks all before it
        </p>
      </div>

      <div className="border-t border-white/10 px-4 py-4">
        <button
          type="button"
          data-testid="factory-apply-status-button"
          onClick={apply}
          disabled={busy || unchanged}
          className="mono flex h-9 w-full items-center justify-center gap-1.5 rounded-full bg-cyan-600 text-[11px] uppercase tracking-wider text-white transition-colors hover:bg-cyan-500 disabled:opacity-40"
        >
          {busy ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            `Apply to ${selection.length} panels`
          )}
        </button>
        {unchanged ? (
          <p
            data-testid="factory-no-change-hint"
            className="mono mt-2 text-center text-[9.5px] uppercase tracking-wider text-slate-500"
          >
            Already at this stage
          </p>
        ) : null}
      </div>
    </aside>
  );
}
